import LeadershipService from '../../api/leadership';
import filteringHelper from 'src/services/filteringHelper';
import { formatEntityName } from 'src/services/textFormatter';
import Vue from 'vue';
import _ from 'lodash';

// initial state
const state = {
  leadersData: {},
  loadState: {
    publication: false,
    patent: false,
  },
  leadersBy: {
    publication: 'doc_count',
    patent: 'doc_count',
  },
};

const getters = {
  getLeadersData: (state) => {
    // Clone leaders data to avoid mutating state
    const publicationData = state.leadersData.publication
      ? _.cloneDeep(state.leadersData.publication)
      : null;
    const patentData = state.leadersData.patent
      ? _.cloneDeep(state.leadersData.patent)
      : null;

    const leadersData = {};
    if (patentData) {
      // Sort patent leaders data by the leadersBy field and format entity names
      Object.keys(patentData).forEach((leaderField) => {
        patentData[leaderField]
          .sort((a, b) => b[state.leadersBy.patent] - a[state.leadersBy.patent])
          .forEach((item) => {
            // Format entity names if they are strings and not country or state as they are already formatted
            item.name =
              typeof item.name === 'string' &&
              !leaderField.includes('country') &&
              !leaderField.includes('state')
                ? formatEntityName(item.name)
                : item.name;
          });
      });
      // Added sorted/formatted data to leadersData
      leadersData.patent = patentData;
    }

    if (publicationData) {
      // Sort publication leaders data by the leadersBy field
      Object.keys(publicationData).forEach((leaderField) => {
        publicationData[leaderField].sort(
          (a, b) =>
            b[state.leadersBy.publication] - a[state.leadersBy.publication]
        );
      });
      // Add sorted data to leadersData
      leadersData.publication = publicationData;
    }

    return leadersData;
  },
  loadState: (state) => {
    return state.loadState;
  },
  leadersBy: (state) => {
    return state.leadersBy;
  },
};

const actions = {
  async fetchLeaders({ commit, dispatch, state, rootGetters }, payload) {
    // Payload should be the pagination coming in
    // We will read filtering from state

    // Set source for elasticsearch index by what is included in the payload --OR-- whatever the activeDataSource is
    const source =
      payload && payload.source
        ? payload.source
        : rootGetters['app/activeDataSource'];
    const leadersBy = state.leadersBy[source];

    // Dispatch action to set loadState to false
    dispatch('loadState', { source, data: false });

    // Get scan item (query, scan_id)
    const { query, version_id } = rootGetters['scans/getScanDetails'];
    // Get scan type
    const scanType = rootGetters['scans/scanType'];

    // Set query_builder to query by default
    let query_builder = query;
    let filters =
      rootGetters['filters/currentFiltering'][source] &&
      Object.keys(rootGetters['filters/currentFiltering'][source]).length
        ? rootGetters['filters/currentFiltering'][source]
        : null;

    if (filters) {
      // Process filters based on scan type
      if (scanType === 'boolean') {
        // Check if search_within_results is included in filters object
        if (filters.search_within_results) {
          // Call helper function to add search within results to query
          query_builder = filteringHelper.filterResultsText(
            query,
            filters.search_within_results
          );
        }
      } else {
        // Check if positive or negative filters are included in filters object
        if (filters.positive || filters.negative) {
          // Call helper function to add positive and negative filters to query
          query_builder = filteringHelper.filterSemantic(query_builder, {
            positive: filters.positive,
            negative: filters.negative,
          });
        }
      }
    }

    // Package up the request
    const request = {
      query: query_builder,
      scan_id: version_id,
      scan_type: scanType,
      filters,
      source: source,
      leadersBy: leadersBy,
    };
    const paginationService = new LeadershipService();
    let leaderData = await paginationService.fetchLeaders(request);

    // Remove nullish values from leaders data
    if (leaderData) {
      Object.keys(leaderData).forEach((key) => {
        leaderData[key] = leaderData[key].filter((leader) => leader != null);
      });
    }

    // Commit data from endpoint
    const _payload = {
      data: leaderData,
      source,
    };
    commit('setLeaderData', _payload);
    dispatch('loadState', { source, data: true });
  },
  setLeadersBy({ commit, dispatch }, payload) {
    // Commit mutation to change leadersBy
    commit('setLeadersBy', payload);
    // Dispatch action to fetch leaders data for the new agg field
    dispatch('fetchLeaders', payload);
  },
  loadState({ commit }, payload) {
    // Commit mutation to change load state to true
    commit('setLoadState', payload);
  },
  clearData({ commit }) {
    commit('setInitialState');
  },
};

// mutations
const mutations = {
  setLeaderData(state, payload) {
    // Use Vue.set to fix reactivity issues with nested objects
    Vue.set(state.leadersData, payload.source, payload.data);
  },
  setLoadState(state, payload) {
    // Set loadState for the source
    Vue.set(state.loadState, payload.source, payload.data);
  },
  setLeadersBy(state, payload) {
    Vue.set(state.leadersBy, payload.source, payload.data);
  },
  setInitialState(state) {
    state.leadersData = {};
    state.leadersBy = {
      publication: 'doc_count',
      patent: 'doc_count',
    };
    state.loadState = {
      publication: false,
      patent: false,
    };
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
